"use client";

import { c as _c } from "react/compiler-runtime";
import { motion, useTransform, MotionValue } from "framer-motion";
import Image from "next/image";
import Link from "next/link";
import { ArrowRight } from "lucide-react";
import { Button } from "@/shared/ui/button";
import type { Service } from "@/features/services/constants/services-data";
interface ServiceCardProps {
  service: Service;
  index: number;
  total: number;
  parentScrollProgress: MotionValue<number>;
}
export function ServiceCard(t0) {
  const $ = _c(64);
  const {
    service,
    index,
    total,
    parentScrollProgress
  } = t0;
  const isLast = index === total - 1;
  const step = 1 / (total - 1);
  const start = index * step;
  const end = (index + 1) * step;
  let t1;
  if ($[0] !== end || $[1] !== start) {
    t1 = [start, end];
    $[0] = end;
    $[1] = start;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [1, 0.92];
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const scale = useTransform(parentScrollProgress, t1, t2);
  let t3;
  if ($[4] !== end || $[5] !== start) {
    t3 = [start, end];
    $[4] = end;
    $[5] = start;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = [0, 0.6];
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const dimOverlay = useTransform(parentScrollProgress, t3, t4);
  let t5;
  if ($[8] !== end || $[9] !== start) {
    t5 = [start, end];
    $[8] = end;
    $[9] = start;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = [0, 24];
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const radius = useTransform(parentScrollProgress, t5, t6);
  let t7;
  if ($[12] !== end || $[13] !== start) {
    t7 = val => {
      if (val <= start) {
        return "blur(0px)";
      }
      if (val >= end) {
        return "blur(8px)";
      }
      const ratio = (val - start) / (end - start);
      const blur = ratio * 8;
      return `blur(${blur.toFixed(2)}px)`;
    };
    $[12] = end;
    $[13] = start;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const filterStyle = useTransform(parentScrollProgress, t7);
  const t8 = index + 1;
  let t9;
  if ($[15] !== t8) {
    t9 = {
      zIndex: t8
    };
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  const t10 = isLast ? 1 : scale;
  const t11 = isLast ? 0 : radius;
  const t12 = isLast ? "none" : filterStyle;
  let t13;
  if ($[17] !== t10 || $[18] !== t11 || $[19] !== t12) {
    t13 = {
      scale: t10,
      borderRadius: t11,
      filter: t12
    };
    $[17] = t10;
    $[18] = t11;
    $[19] = t12;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  const t14 = index < 2;
  let t15;
  if ($[21] !== service.imageUrl || $[22] !== service.title || $[23] !== t14) {
    t15 = <Image src={service.imageUrl} alt={service.title} fill={true} priority={t14} sizes="100vw" className="object-cover object-center brightness-125 contrast-105 grayscale-[20%]" />;
    $[21] = service.imageUrl;
    $[22] = service.title;
    $[23] = t14;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  let t16;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/25 to-transparent" />;
    $[25] = t16;
  } else {
    t16 = $[25];
  }
  let t17;
  if ($[26] !== t15) {
    t17 = <div className="absolute inset-0 z-0">{t15}{t16}</div>;
    $[26] = t15;
    $[27] = t17;
  } else {
    t17 = $[27];
  }
  let t18;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <div className="h-14" />;
    $[28] = t18;
  } else {
    t18 = $[28];
  }
  let t19;
  if ($[29] !== service.title) {
    t19 = <h2 className="mb-8 font-heading text-[clamp(2.5rem,6vw,5rem)] leading-[1.05] font-bold tracking-tight text-white">{service.title}</h2>;
    $[29] = service.title;
    $[30] = t19;
  } else {
    t19 = $[30];
  }
  const t20 = `/en/services/${service.slug}`;
  let t21;
  if ($[31] !== t20) {
    t21 = <Link href={t20} />;
    $[31] = t20;
    $[32] = t21;
  } else {
    t21 = $[32];
  }
  let t22;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <ArrowRight className="transition-transform duration-300 group-hover:translate-x-0.5" />;
    $[33] = t22;
  } else {
    t22 = $[33];
  }
  let t23;
  if ($[34] !== t21) {
    t23 = <Button size="lg" render={t21} className="group border-white/25 bg-white/8 text-white backdrop-blur-sm hover:border-white/60 [:hover,[data-pressed]]:bg-white/15">Explore More{t22}</Button>;
    $[34] = t21;
    $[35] = t23;
  } else {
    t23 = $[35];
  }
  let t24;
  if ($[36] !== t19 || $[37] !== t23) {
    t24 = <div className="my-auto max-w-3xl">{t19}{t23}</div>;
    $[36] = t19;
    $[37] = t23;
    $[38] = t24;
  } else {
    t24 = $[38];
  }
  let t25;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <p className="mb-2 text-[10px] font-bold tracking-[0.2em] text-white uppercase">Overview</p>;
    $[39] = t25;
  } else {
    t25 = $[39];
  }
  let t26;
  if ($[40] !== service.overview) {
    t26 = <div className="sm:col-span-5">{t25}<p className="text-sm leading-relaxed text-white md:text-[15px]">{service.overview}</p></div>;
    $[40] = service.overview;
    $[41] = t26;
  } else {
    t26 = $[41];
  }
  let t27;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <p className="mb-2 text-[10px] font-bold tracking-[0.2em] text-white uppercase">Recommended for</p>;
    $[42] = t27;
  } else {
    t27 = $[42];
  }
  let t28;
  if ($[43] !== service.recommendedFor) {
    t28 = service.recommendedFor.map(_temp);
    $[43] = service.recommendedFor;
    $[44] = t28;
  } else {
    t28 = $[44];
  }
  let t29;
  if ($[45] !== t28) {
    t29 = <div className="sm:col-span-6 flex md:justify-end"><div>{t27}<ul className="columns-1 space-y-1 text-sm text-white md:columns-2">{t28}</ul></div></div>;
    $[45] = t28;
    $[46] = t29;
  } else {
    t29 = $[46];
  }
  let t30;
  if ($[47] !== t26 || $[48] !== t29) {
    t30 = <div className="grid grid-cols-1 gap-6 border-t border-white/10 pt-6 text-white sm:grid-cols-12 sm:gap-8 sm:pt-8">{t26}{t29}</div>;
    $[47] = t26;
    $[48] = t29;
    $[49] = t30;
  } else {
    t30 = $[49];
  }
  let t31;
  if ($[50] !== t24 || $[51] !== t30) {
    t31 = <div className="relative z-10 flex h-full w-full flex-col justify-between p-6 sm:p-10 md:p-16 lg:p-20">{t18}{t24}{t30}</div>;
    $[50] = t24;
    $[51] = t30;
    $[52] = t31;
  } else {
    t31 = $[52];
  }
  let t32;
  if ($[53] !== dimOverlay || $[54] !== isLast) {
    t32 = !isLast && <motion.div className="pointer-events-none absolute inset-0 z-[5] bg-black" style={{
      opacity: dimOverlay
    }} />;
    $[53] = dimOverlay;
    $[54] = isLast;
    $[55] = t32;
  } else {
    t32 = $[55];
  }
  let t33;
  if ($[56] !== t13 || $[57] !== t17 || $[58] !== t31 || $[59] !== t32) {
    t33 = <motion.div className="relative mx-auto h-full w-full origin-center overflow-hidden will-change-transform" style={t13}>{t17}{t31}{t32}</motion.div>;
    $[56] = t13;
    $[57] = t17;
    $[58] = t31;
    $[59] = t32;
    $[60] = t33;
  } else {
    t33 = $[60];
  }
  let t34;
  if ($[61] !== t33 || $[62] !== t9) {
    t34 = <div className="sticky top-0 h-[100svh] w-full overflow-hidden bg-background" style={t9}>{t33}</div>;
    $[61] = t33;
    $[62] = t9;
    $[63] = t34;
  } else {
    t34 = $[63];
  }
  return t34;
}
function _temp(item, i) {
  return <li key={i} className="flex items-baseline gap-2 leading-snug"><span className="mt-1 h-1 w-1 shrink-0 rounded-full bg-white/40" />{item}</li>;
}